import React, { useState, useEffect } from 'react';
import axios from 'axios';
import AdminLayout from '../../components/admin/AdminLayout';
import { Leaf, Plus, Trash2, Save, AlertCircle, Upload, X, Image as ImageIcon } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Textarea } from '@/components/ui/textarea';
import { Input } from '@/components/ui/input';
import { getImageUrl } from '@/utils/assetUrlHelper';
import FileUploadInput from '../../components/admin/FileUploadInput';

const API_URL = process.env.REACT_APP_BACKEND_URL || '';

export default function VKSAManagement() {
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [data, setData] = useState(null);
  const [error, setError] = useState('');
  const [fileData, setFileData] = useState({});

  useEffect(() => {
    fetchData();
  }, []);

  const getAuthHeaders = () => {
    const token = localStorage.getItem('cish_token');
    return { Authorization: `Bearer ${token}` };
  };

  const fetchData = async () => {
    try {
      setLoading(true);
      const response = await axios.get(`${API_URL}/api/vksa`);
      setData(response.data);
    } catch (error) {
      console.error('Error fetching data:', error);
      if (error.response?.status === 404) {
        setData({
          id: 'vksa-1',
          page_title: 'VKSA (Viksit Krishi Sankalp Abhiyan)',
          intro_text: '',
          items: []
        });
      }
    } finally {
      setLoading(false);
    }
  };

  const uploadFile = async (file, subfolder) => {
    const formData = new FormData();
    formData.append('file', file);
    // Use query parameter for subfolder
    const res = await axios.post(`${API_URL}/api/files/upload?subfolder=${subfolder}`, formData, {
      headers: { ...getAuthHeaders(), 'Content-Type': 'multipart/form-data' }
    });
    return `/api/files/${res.data.id}`;
  };

  const handleSave = async () => {
    try {
      setSaving(true);
      setError('');

      const updatedItems = [...data.items];

      // Process uploads for each item
      for (let i = 0; i < updatedItems.length; i++) {
        const item = updatedItems[i];

        // Thumbnail
        if (fileData[`${item.id}_thumbnail`]) {
          const url = await uploadFile(fileData[`${item.id}_thumbnail`], 'vksa/thumbnails');
          updatedItems[i].thumbnail_image = url;
        }

        // Gallery
        if (fileData[`${item.id}_gallery`]) {
          const files = fileData[`${item.id}_gallery`];
          try {
            const uploadPromises = files.map(async f => {
              try {
                return await uploadFile(f, 'vksa/gallery');
              } catch (e) {
                console.error("Failed to upload file", f.name, e);
                return null;
              }
            });
            const results = await Promise.all(uploadPromises);
            const urls = results.filter(u => u !== null);
            
            if (urls.length < files.length) {
               console.warn(`Some images failed to upload. Saved ${urls.length} of ${files.length}.`);
            }
            updatedItems[i].gallery_images = [...(updatedItems[i].gallery_images || []), ...urls];
          } catch (err) {
            console.error("Error processing gallery uploads:", err);
          }
        }

        // PDF
        if (fileData[`${item.id}_pdf`]) {
          const file = fileData[`${item.id}_pdf`];
          const url = await uploadFile(file, 'vksa/pdfs');
          updatedItems[i].pdf_file = url;
          updatedItems[i].pdf_filename = file.name;
        }
      }

      await axios.post(`${API_URL}/api/vksa`, { ...data, items: updatedItems }, {
        headers: getAuthHeaders()
      });

      setFileData({});
      setData({ ...data, items: updatedItems });
      alert('VKSA page updated successfully!');
    } catch (error) {
      console.error('Error saving:', error);
      setError(error.response?.data?.detail || 'Failed to save data');
    } finally {
      setSaving(false);
    }
  };

  const addItem = () => {
    setData({
      ...data,
      items: [
        ...data.items,
        {
          id: `vksa-item-${Date.now()}`,
          thumbnail_image: null,
          title: '',
          description: '',
          date: '',
          end_date: '',
          gallery_images: [],
          pdf_file: null,
          pdf_filename: null
        }
      ]
    });
  };

  const updateItem = (index, field, value) => {
    const updated = [...data.items];
    updated[index][field] = value;
    setData({ ...data, items: updated });
  };

  const deleteItem = (index) => {
    setData({
      ...data,
      items: data.items.filter((_, i) => i !== index)
    });
  };

  if (loading) {
    return (
      <AdminLayout>
        <div className="flex items-center justify-center h-screen">
          <div className="text-lg">Loading...</div>
        </div>
      </AdminLayout>
    );
  }

  if (!data) {
    return (
      <AdminLayout>
        <div className="flex items-center justify-center h-screen">
          <div className="text-lg text-red-600">Failed to load data</div>
        </div>
      </AdminLayout>
    );
  }

  return (
    <AdminLayout>
      <div className="p-6 max-w-7xl mx-auto">
        <div className="flex items-center justify-between mb-6">
          <div>
            <h1 className="text-3xl font-bold text-gray-900 flex items-center gap-2">
              <Leaf className="w-8 h-8 text-[#0E6D2B]" />
              VKSA Management
            </h1>
            <p className="text-gray-600 mt-1">Manage Viksit Krishi Sankalp Abhiyan activities</p>
          </div>
          <Button
            onClick={handleSave}
            disabled={saving}
            className="bg-[#0E6D2B] hover:bg-[#0a5a23]"
          >
            <Save className="w-4 h-4 mr-2" />
            {saving ? 'Saving...' : 'Save Changes'}
          </Button>
        </div>

        {error && (
          <div className="bg-red-50 border border-red-200 rounded-lg p-4 mb-6 flex items-start gap-2">
            <AlertCircle className="w-5 h-5 text-red-600 flex-shrink-0 mt-0.5" />
            <p className="text-red-800">{error}</p>
          </div>
        )}

        {/* Page Settings */}
        <div className="bg-white rounded-lg shadow-sm border p-6 mb-6">
          <h2 className="text-xl font-bold mb-4">Page Settings</h2>
          <div className="space-y-4">
            <div>
              <label className="block text-sm font-medium mb-1">Page Title</label>
              <Input
                value={data.page_title}
                onChange={(e) => setData({ ...data, page_title: e.target.value })}
              />
            </div>
            <div>
              <label className="block text-sm font-medium mb-1">Intro Text (HTML)</label>
              <Textarea
                value={data.intro_text}
                onChange={(e) => setData({ ...data, intro_text: e.target.value })}
                rows={3}
                placeholder="<p>Enter HTML content...</p>"
              />
            </div>
          </div>
        </div>

        {/* VKSA Items */}
        <div className="bg-white rounded-lg shadow-sm border p-6 mb-6">
          <div className="flex items-center justify-between mb-4">
            <h2 className="text-xl font-bold">VKSA Activities</h2>
            <Button onClick={addItem} size="sm" variant="outline">
              <Plus className="w-4 h-4 mr-1" />
              Add Activity
            </Button>
          </div>

          <div className="space-y-6">
            {data.items.map((item, idx) => (
              <div key={item.id} className="border rounded-lg p-6 bg-gray-50">
                <div className="flex items-center justify-between mb-4">
                  <h3 className="font-bold text-gray-700">Activity {idx + 1}</h3>
                  <button
                    onClick={() => deleteItem(idx)}
                    className="text-red-600 hover:text-red-800"
                  >
                    <Trash2 className="w-5 h-5" />
                  </button>
                </div>

                <div className="grid md:grid-cols-2 gap-4 mb-4">
                  {/* Title */}
                  <div className="md:col-span-2">
                    <label className="block text-sm font-medium mb-1">Title *</label>
                    <Input
                      value={item.title}
                      onChange={(e) => updateItem(idx, 'title', e.target.value)}
                      placeholder="Activity title"
                    />
                  </div>

                  {/* Start Date */}
                  <div>
                    <label className="block text-sm font-medium mb-1">Start Date (DD/MM/YYYY) *</label>
                    <Input
                      value={item.date}
                      onChange={(e) => updateItem(idx, 'date', e.target.value)}
                      placeholder="DD/MM/YYYY"
                    />
                  </div>

                  {/* End Date */}
                  <div>
                    <label className="block text-sm font-medium mb-1">End Date (DD/MM/YYYY) <span className="text-gray-400">(Optional)</span></label>
                    <Input
                      value={item.end_date || ''}
                      onChange={(e) => updateItem(idx, 'end_date', e.target.value)}
                      placeholder="DD/MM/YYYY"
                    />
                  </div>

                  {/* Thumbnail Upload */}
                  <div className="md:col-span-2">
                    <label className="block text-sm font-medium mb-2">Thumbnail Image</label>
                    <FileUploadInput
                      field={{ name: 'thumbnail_image', type: 'file', fileType: 'image' }}
                      value={fileData[`${item.id}_thumbnail`]}
                      existingUrl={item.thumbnail_image}
                      onChange={(files) => setFileData({ ...fileData, [`${item.id}_thumbnail`]: files })}
                      onDeleteServerFile={() => { updateItem(idx, 'thumbnail_image', null); return true; }}
                    />
                  </div>
                </div>

                {/* Description */}
                <div className="mb-4">
                  <label className="block text-sm font-medium mb-1">Description (HTML) *</label>
                  <Textarea
                    value={item.description}
                    onChange={(e) => updateItem(idx, 'description', e.target.value)}
                    rows={4}
                    placeholder="<p>Activity description...</p>"
                  />
                </div>

                {/* Gallery Images */}
                <div className="mb-4">
                  <label className="block text-sm font-medium mb-2">Gallery Images</label>
                  <FileUploadInput
                    field={{ name: 'gallery_images', type: 'file', fileType: 'image', multiple: true }}
                    value={fileData[`${item.id}_gallery`]}
                    existingUrl={item.gallery_images}
                    onChange={(files) => setFileData({ ...fileData, [`${item.id}_gallery`]: files })}
                    onDeleteServerFile={(url) => {
                      const updated = [...data.items];
                      updated[idx].gallery_images = updated[idx].gallery_images.filter(u => {
                        const uPath = u.replace(/^https?:\/\/[^\/]+/, '');
                        const urlPath = url.replace(/^https?:\/\/[^\/]+/, '');
                        return uPath !== urlPath && u !== url;
                      });
                      setData({ ...data, items: updated });
                      return true;
                    }}
                  />
                </div>

                {/* PDF Upload */}
                <div>
                  <label className="block text-sm font-medium mb-2">PDF Document (Optional)</label>
                  {item.pdf_filename && <p className="text-sm text-gray-600 mb-1">Current File: {item.pdf_filename}</p>}
                  <FileUploadInput
                    field={{ name: 'pdf_file', type: 'file', fileType: 'document' }}
                    value={fileData[`${item.id}_pdf`]}
                    existingUrl={item.pdf_file}
                    onChange={(files) => setFileData({ ...fileData, [`${item.id}_pdf`]: files })}
                    onDeleteServerFile={() => {
                      updateItem(idx, 'pdf_file', null);
                      updateItem(idx, 'pdf_filename', null);
                      return true;
                    }}
                  />
                </div>
              </div>
            ))}

            {data.items.length === 0 && (
              <div className="text-center py-12 text-gray-500">
                No activities added yet. Click "Add Activity" to create your first VKSA item.
              </div>
            )}
          </div>
        </div>

        {/* Save Button at Bottom */}
        <div className="flex justify-end">
          <Button
            onClick={handleSave}
            disabled={saving}
            className="bg-[#0E6D2B] hover:bg-[#0a5a23]"
          >
            <Save className="w-4 h-4 mr-2" />
            {saving ? 'Saving...' : 'Save All Changes'}
          </Button>
        </div>
      </div>
    </AdminLayout>
  );
}
